import { useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { fmtQty, GRADES, type GoodsReceiptRecord } from "@/types/inventory";

const day = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");

/** One receipt with its lines and rolls; can be cancelled while its rolls are untouched */
export function ReceiptDetailDialog({ receiptId, onClose, onChanged }: { receiptId: string | null; onClose: () => void; onChanged: () => void }) {
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const [g, setG] = useState<GoodsReceiptRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!receiptId) return;
    setLoading(true);
    api
      .get(`/api/GoodsReceipt/ById/${receiptId}`)
      .then((r) => setG(unwrapObject<GoodsReceiptRecord>(r)))
      .catch(() => setG(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    setG(null);
    setCancelling(false);
    setReason("");
    load();
  }, [receiptId]);

  const cancel = async () => {
    if (!g) return;
    if (!reason.trim()) return error("Validation Error", "Give a reason for cancelling");
    setBusy(true);
    try {
      await api.patch(`/api/GoodsReceipt/Cancel/${g.id}`, { reason: reason.trim() });
      success("Receipt cancelled", "Its rolls were taken back out of stock");
      setCancelling(false);
      load();
      onChanged();
    } catch (err: any) {
      error("Could not cancel", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const lotOf = (lotId: string) => g?.lines.find((l) => l.lot_id === lotId);
  return (
    <Dialog open={!!receiptId} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {g ? g.grn_number : "Receipt"}
            {g?.receipt_status === "CANCELLED" && <Badge variant="outline">Cancelled</Badge>}
          </DialogTitle>
          <DialogDescription>{g ? `${g.receipt_type === "OPENING" ? "Opening stock" : g.supplier?.name || "Purchase"} · ${day(g.receipt_date)} · ${g.warehouse?.name || ""}` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {loading && !g && (
          <div className="py-10 flex justify-center">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {g && (
          <div className="space-y-4 text-sm overflow-y-auto max-h-[68vh] pr-1">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div><p className="text-muted-foreground">Supplier invoice</p><p className="font-semibold">{g.supplier_invoice_no || "-"}</p></div>
              <div><p className="text-muted-foreground">Challan</p><p className="font-semibold">{g.supplier_challan_no || "-"}</p></div>
              <div><p className="text-muted-foreground">Transporter</p><p className="font-semibold">{g.transporter || "-"}</p></div>
              <div><p className="text-muted-foreground">Vehicle</p><p className="font-semibold">{g.vehicle_number || "-"}</p></div>
            </div>
            {g.notes && <p className="text-xs text-muted-foreground">{g.notes}</p>}
            {g.cancelled_reason && <p className="text-xs rounded-lg bg-muted px-3 py-2">Cancelled: {g.cancelled_reason}</p>}

            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="px-3 py-2">Product / shade</th>
                    <th className="px-3 py-2">Lot</th>
                    <th className="px-3 py-2 text-right">Rolls</th>
                    <th className="px-3 py-2 text-right">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {g.lines.map((l) => (
                    <tr key={l.id} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-2">{l.product?.name}{l.variant ? ` · ${l.variant.name}` : ""}</td>
                      <td className="px-3 py-2">{l.lot?.lot_number}</td>
                      <td className="px-3 py-2 text-right">{l.unit_count}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtQty(l.quantity, l.unit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <details className="rounded-lg border border-border">
              <summary className="px-3 py-2 text-xs font-semibold cursor-pointer">Rolls ({g.units?.length || 0})</summary>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground border-y border-border">
                      <th className="px-3 py-1.5">Roll</th>
                      <th className="px-3 py-1.5">Lot</th>
                      <th className="px-3 py-1.5 text-right">Received</th>
                      <th className="px-3 py-1.5 text-right">Left</th>
                      <th className="px-3 py-1.5">Grade</th>
                      <th className="px-3 py-1.5">Rack</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(g.units || []).map((u) => (
                      <tr key={u.id} className="border-b border-border/60 last:border-0">
                        <td className="px-3 py-1.5 font-mono font-semibold">{u.unit_no}</td>
                        <td className="px-3 py-1.5">{lotOf(u.lot_id)?.lot?.lot_number}</td>
                        <td className="px-3 py-1.5 text-right">{fmtQty(u.initial_quantity, u.unit)}</td>
                        <td className="px-3 py-1.5 text-right">{fmtQty(u.quantity)}</td>
                        <td className="px-3 py-1.5">{GRADES[u.grade] || u.grade}</td>
                        <td className="px-3 py-1.5">{u.location || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            {g.receipt_status === "RECEIVED" && hasPermission("inventory.adjust") && (
              <div className="pt-1">
                {!cancelling ? (
                  <Button variant="outline" size="sm" className="text-destructive" onClick={() => setCancelling(true)}>
                    Cancel this receipt
                  </Button>
                ) : (
                  <div className="space-y-2 rounded-lg border border-destructive/40 p-3">
                    <p className="text-xs text-muted-foreground">This takes every roll on the receipt back out of stock. It only works while none of them has been used or cut.</p>
                    <Textarea rows={2} placeholder="Why is it being cancelled?" value={reason} onChange={(e) => setReason(e.target.value)} />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setCancelling(false)} disabled={busy}>Keep it</Button>
                      <Button variant="destructive" size="sm" onClick={cancel} disabled={busy} className="gap-1.5">
                        {busy && <Loader2 className="size-3.5 animate-spin" />}
                        Cancel receipt
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
